import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, User, Target, Ruler, Trash2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import db from '../db';

const More = () => {
  const { user, goals, setGoals } = useApp();
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [draftGoals, setDraftGoals] = useState(goals);

  const saveGoals = () => {
    setGoals(draftGoals);
    setGoalsOpen(false);
  };

  const toggleUnits = () => {
    const raw = localStorage.getItem('trackr_profile');
    if (!raw) return;
    const profile = JSON.parse(raw);
    const newUnits = profile.units === 'metric' ? 'imperial' : 'metric';
    setUser({ ...profile, units: newUnits });
  };

  const resetData = async () => {
    if (!window.confirm('Delete all your diary, exercise, weight and water data? This cannot be undone.')) return;
    await Promise.all([db.diary.clear(), db.exercise.clear(), db.weight.clear(), db.water.clear()]);
    window.location.reload();
  };

  const resetAll = () => {
    if (!window.confirm('Reset everything including your profile? You will go through onboarding again.')) return;
    logout();
  };

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">
      <header className="bg-[#0066ee] text-white px-5 pt-5 pb-8 rounded-b-3xl">
        <h1 className="text-xl font-bold">More</h1>
        <button onClick={() => navigate('/profile')} className="mt-4 flex items-center gap-3 w-full text-left press-scale">
          <img src={user.avatar} alt={user.name} className="w-14 h-14 rounded-full border-2 border-white object-cover" />
          <div className="flex-1">
            <p className="font-bold">{user.name}</p>
            <p className="text-xs text-blue-100">{user.username} · {user.streak}-day streak</p>
          </div>
          <ChevronRight size={20} className="text-white/70" />
        </button>
      </header>

      <div className="px-5 -mt-4 pb-6 space-y-4">

        {/* YOU */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden slide-up">
          <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">You</p>
          <ul>
            <MenuItem icon={<User size={17} />} color="bg-blue-50 text-[#0066ee]" label="Profile" onPress={() => navigate('/profile')} />

            {/* Goals — expandable */}
            <li>
              <button
                onClick={() => { setDraftGoals(goals); setGoalsOpen((o) => !o); }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 press-scale text-left"
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600">
                  <Target size={17} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Goals</p>
                  <p className="text-xs text-gray-500">{goals.calories} cal · P{goals.protein}g C{goals.carbs}g F{goals.fat}g</p>
                </div>
                {goalsOpen ? <ChevronUp size={18} className="text-gray-300" /> : <ChevronDown size={18} className="text-gray-300" />}
              </button>

              {goalsOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-50">
                  {[
                    { key: 'calories', label: 'Calories (cal)', max: 5000 },
                    { key: 'protein', label: 'Protein (g)', max: 300 },
                    { key: 'carbs', label: 'Carbs (g)', max: 500 },
                    { key: 'fat', label: 'Fat (g)', max: 200 },
                    { key: 'water', label: 'Water (cups)', max: 20 },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
                      <input
                        type="number"
                        value={draftGoals[key]}
                        onChange={(e) => setDraftGoals((d) => ({ ...d, [key]: parseInt(e.target.value) || 0 }))}
                        className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold outline-none focus:border-[#0066ee]"
                      />
                    </div>
                  ))}
                  <div className="flex gap-3 pt-1">
                    <button onClick={() => setGoalsOpen(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700">
                      Cancel
                    </button>
                    <button onClick={saveGoals} className="flex-1 py-2.5 rounded-xl bg-[#0066ee] text-white text-sm font-bold">
                      Save Goals
                    </button>
                  </div>
                </div>
              )}
            </li>

            {/* Units toggle */}
            <li>
              <button
                onClick={toggleUnits}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 press-scale text-left"
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-50 text-orange-500">
                  <Ruler size={17} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Units</p>
                  <p className="text-xs text-gray-500">Currently: {user.units === 'metric' ? 'Metric (kg, cm)' : 'Imperial (lb, ft/in)'}</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${user.units === 'metric' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {user.units === 'metric' ? 'Metric' : 'Imperial'}
                </span>
              </button>
            </li>
          </ul>
        </div>

        {/* DATA */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden slide-up">
          <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">Data</p>
          <ul>
            <MenuItem
              icon={<Trash2 size={17} />}
              color="bg-red-50 text-red-500"
              label="Clear Activity Data"
              subtitle="Keeps your profile, clears diary/exercise/weight"
              onPress={resetData}
            />
            <MenuItem
              icon={<Trash2 size={17} />}
              color="bg-red-100 text-red-700"
              label="Reset Everything"
              subtitle="Removes all data and restarts onboarding"
              onPress={resetAll}
            />
          </ul>
        </div>

        {/* ABOUT */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden slide-up">
          <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">About</p>
          <ul>
            <li className="flex items-center gap-3 px-4 py-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gray-100 text-gray-500">
                <Info size={17} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Trackr</p>
                <p className="text-xs text-gray-500">v1.0 · Free, private, offline-first PWA</p>
              </div>
            </li>
          </ul>
        </div>

      </div>
    </div>
  );
};

const MenuItem = ({ icon, color, label, subtitle, onPress }) => (
  <li>
    <button
      onClick={onPress}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 press-scale text-left"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      <ChevronRight size={18} className="text-gray-300" />
    </button>
  </li>
);

export default More;
